<template>
  <component
    v-bind="$attrs"
    :is="type"
    :class="[$attrs.class, b(modifiers)]"
    @click="onClick"
  >
    <e-icon-sprite
      :icon="icon"
      :class="b('icon')"
    />
    <div
      v-if="$slots.default"
      :class="b('content')"
    >
      <slot></slot>
    </div>
  </component>
</template>

<script>
  import eIconSprite from '@/elements/e-icon-sprite.vue';

  /**
   * Renders a `<button>` or `<a>` element (based on existing `href` attribute)
   * combined with an icon for which the position can be defined.
   * The component uses a `<slot>` to render the content.
   *
   * [You can also define inherited attributes for `<button>`](https://developer.mozilla.org/de/docs/Web/HTML/Element/button#Attribute)
   *
   * [You can also define inherited attributes for `<a>`](https://developer.mozilla.org/de/docs/Web/HTML/Element/a#Attribute)
   */
  export default {
    name: 'e-icon-cta',
    components: {
      eIconSprite,
    },
    inheritAttrs: false,

    // mixins: [],

    props: {
      /**
       * The icon name.
       */
      icon: {
        type: String,
        default: '',
      },

      /**
       * Allows to use a different button style.
       */
      variant: {
        type: String,
        default: null,
        validator(value) {
          return ['primary', 'secondary', 'negative'].includes(value);
        },
      },

      /**
       * Allows to use a different button type.
       */
      buttonType: {
        type: String,
        default: null,
        validator(value) {
          return ['default', 'circle'].includes(value);
        },
      },

      /**
       * Allows to add inner spacing to the cta.
       */
      innerSpacing: {
        type: [Number, String],
        default: 0,
        validator(value) {
          return [0, 500].includes(Number(value));
        },
      },
    },

    emits: ['click'],

    // data() {},

    computed: {
      /**
       * Gets element type.
       *
       * @returns {String}
       */
      type() {
        return this.$attrs.href ? 'a' : 'button';
      },

      /**
       * Returns an Object of class modifiers.
       *
       * @returns {Object}
       */
      modifiers() {
        return {
          variant: this.variant,
          innerSpacing: this.innerSpacing,
          buttonType: this.buttonType,
        };
      },
    },
    // watch: {},

    // beforeCreate() {},
    // created() {},
    // beforeMount() {},
    // mounted() {},
    // beforeUpdate() {},
    // updated() {},
    // activated() {},
    // deactivated() {},
    // beforeUnmount() {},
    // unmounted() {},

    methods: {
      /**
       * Click event handler.
       *
       * @param {Event} event - The click event instance.
       */
      onClick(event) {
        this.$el.blur();

        this.$emit('click', event);
      },
    },
  };
</script>

<style lang="scss">
  @use '@/setup/scss/variables';
  @use '@/setup/scss/mixins';
  @use '@/setup/scss/functions';
  @use '@/setup/scss/extends' as *;

  .e-icon-cta {
    $this: &;

    @extend %button-reset;
    @include mixins.font(variables.$font-size--16);

    display: flex;
    align-items: center;
    color: functions.get-theme-var('e-icon-cta-color');

    &:focus,
    &:hover {
      color: functions.get-theme-var('e-icon-cta-color--focus');
    }

    &:disabled {
      cursor: default;
      color: functions.get-theme-var('e-icon-cta-color--disabled');

      #{$this}__icon {
        color: functions.get-theme-var('e-icon-cta-color--disabled');
      }
    }

    &__icon {
      width: functions.rem(variables.$spacing--40);
      height: functions.rem(variables.$spacing--40);
    }

    &__content {
      margin-left: functions.rem(variables.$spacing--10);
    }

    &--variant-secondary {
      color: variables.$color-primary--2;

      &:focus,
      &:hover {
        color: variables.$color-primary--2;
      }
    }

    &--variant-negative {
      background: variables.$color-grayscale--1000;
      color: variables.$color-primary--1;

      &:focus,
      &:hover {
        color: variables.$color-primary--2;
      }
    }

    &--inner-spacing-500 {
      padding-top: functions.rem(variables.$spacing--15);
      padding-bottom: functions.rem(variables.$spacing--15);

      @include mixins.media(sm) {
        padding-top: functions.rem(variables.$spacing--20);
        padding-bottom: functions.rem(variables.$spacing--20);
      }
    }

    &--button-type-circle {
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      width: 2em;
      height: 2em;
      padding: functions.rem(variables.$spacing--15);
      border-radius: 50%;

      @include mixins.media(sm) {
        padding: functions.rem(variables.$spacing--20);
      }

      #{$this}__icon {
        display: block;
        flex-shrink: 0;
        width: functions.rem(16px);
        height: functions.rem(16px);

        @include mixins.media(sm) {
          width: functions.rem(20px);
          height: functions.rem(20px);
        }
      }

      #{$this}__content {
        display: none;
      }
    }
  }
</style>
